import React, { useState, useEffect } from 'react';
import { Modal, Typography, Row, Col, Avatar, Button, Divider, message, Spin, Form, Input, Switch, Space, InputNumber, Table, Tag, Card, Statistic, Select, Tooltip } from 'antd';
import { downloadFromS3Signed, getLocation, placesAutocomplete } from 'src/_shared/services/utils.js';
import { UserOutlined, EditOutlined, SaveOutlined, CloseOutlined, EnvironmentOutlined, DollarOutlined, PlusOutlined, GlobalOutlined, SyncOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'motion/react';
import { useLayer, Arrow } from 'react-laag';
import dayjs from 'dayjs';
import { withApollo } from 'react-apollo';
// import { useEmployeeData } from 'src/_shared/hooks/useEmployeeData';
//import { useQueryClient } from '@tanstack/react-query';
import './EmployeeDetailsModal.css';

const { Title, Text, Paragraph } = Typography;

const EditableField = ({ label, value, onSave, loading, editable = true }) => {
  return (
    <div className="editable-field">
      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>{label}</Text>
      <Paragraph
        editable={editable ? {
          tooltip: 'Click to edit',
          onChange: (newValue) => onSave(newValue),
          icon: <EditOutlined />,
          enterIcon: <SaveOutlined />
        } : false}
        style={{
          marginBottom: 0,
          padding: '4px 8px',
          borderRadius: 4,
          backgroundColor: editable ? 'rgba(0, 0, 0, 0.02)' : 'transparent'
        }}
      >
        {loading ? <Spin size="small" /> : (value || 'Not specified')}
      </Paragraph>
    </div>
  );
};

// SelectField component for dropdowns
const SelectField = ({ label, value, options, onSave, loading }) => {
  return (
    <div className="editable-field">
      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>{label}</Text>
      <Select
        value={value}
        onChange={onSave}
        style={{ width: '100%' }}
        loading={loading}
        disabled={loading}
        placeholder={`Select ${label}`}
        optionFilterProp="label"
        showSearch
      >
        {options.map(option => (
          <Select.Option key={option.value} value={option.value} label={option.label}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

// Coordinates Popover component using Ant Design's Tooltip instead of react-laag
// This works better with Ant Design Modal which uses portals
const CoordinatesPopover = ({ coordinates, menuColor }) => {
  if (!coordinates || (!coordinates.lat && !coordinates.lon && !coordinates.lng)) {
    return null;
  }
  
  const lat = coordinates.lat || 0;
  const lon = coordinates.lon || coordinates.lng || 0;
  
  const tooltipContent = (
    <div className="coordinates-tooltip">
      <strong>Coordinates:</strong>
      <div>Latitude: {lat.toFixed(6)}</div>
      <div>Longitude: {lon.toFixed(6)}</div>
    </div>
  );
  
  return (
    <Tooltip
      title={tooltipContent}
      placement="right"
      color="white"
      overlayInnerStyle={{
        color: 'rgba(0, 0, 0, 0.85)',
        borderTop: `2px solid ${menuColor}`,
        padding: '8px 12px'
      }}
    >
      <GlobalOutlined
        className="globe-icon"
        style={{ marginLeft: 8, cursor: 'pointer' }}
      />
    </Tooltip>
  );
};

// Location fields component
const LocationFields = ({ location, onChange, loading, menuColor }) => {
  // Default empty location object
  const defaultLocation = {
    zip: '',
    country: '',
    address: '',
    city: '',
    street: '',
    isRemote: false,
    county: '',
    state: ''
  };
  
  // Parse location string or use default
  const parseLocation = (raw) => {
    if (!raw) return defaultLocation;
    if (typeof raw === 'string') {
      try {
        return { ...defaultLocation, ...JSON.parse(raw) };
      } catch {
        return { ...defaultLocation, address: raw };
      }
    }
    return { ...defaultLocation, ...raw };
  };
  
  // Initialize state with parsed location
  const [locationData, setLocationData] = useState(parseLocation(location));
  const [isFocused, setIsFocused] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  
  // Update location data when prop changes
  useEffect(() => {
    setLocationData(parseLocation(location));
  }, [location]);
  
  // Add click outside listener to close suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSuggestions) {
        const dropdowns = document.querySelectorAll('.suggestions-dropdown');
        const inputs = document.querySelectorAll('.autocomplete-wrapper input');
        let clickedInside = false;
        
        // Check if click was inside any dropdown or input
        dropdowns.forEach(dropdown => {
          if (dropdown.contains(event.target)) {
            clickedInside = true;
          }
        });
        
        inputs.forEach(input => {
          if (input === event.target) {
            clickedInside = true;
          }
        });
        
        if (!clickedInside) {
          setShowSuggestions(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);
  
  // Handle field changes
  const handleFieldChange = (field, value) => {
    const updatedLocation = {
      ...locationData,
      [field]: value
    };
    setLocationData(updatedLocation);
    
    // Notify parent with stringified location
    onChange(JSON.stringify(updatedLocation));
  };
  
  // Debounce function for search
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };
  
  // Search for location suggestions using placesAutocomplete
  const searchLocationSuggestions = (query, field) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    // Store the active field without changing focus
    setActiveField(field);
    
    // Enhance search query with other location fields when appropriate
    let enhancedQuery = query;
    if (field === 'city' && locationData.state) {
      enhancedQuery = `${query}, ${locationData.state}`;
    }
    if ((field === 'city' || field === 'state') && locationData.country) {
      enhancedQuery = `${enhancedQuery}, ${locationData.country}`;
    }
    
    // Show loading indicator without blocking input
    // We're not awaiting the API call, so we don't need to set isAutoFilling to true here
    
    // Call the placesAutocomplete API without blocking
    placesAutocomplete(enhancedQuery)
      .then(response => {
        console.log('Autocomplete response:', response); // Debug log
        
        if (response && response.Results && response.Results.length > 0) {
          // Handle new API format with Results array
          const formattedSuggestions = response.Results.map(result => ({
            value: result.Text,
            label: result.Text,
            placeId: result.PlaceId
          }));
          
          setSuggestions(formattedSuggestions);
          setShowSuggestions(formattedSuggestions.length > 0);
        } else if (response && response.predictions && response.predictions.length > 0) {
          // Handle old API format with predictions array
          const formattedSuggestions = response.predictions.map(prediction => ({
            value: prediction.description,
            label: prediction.description,
            placeId: prediction.place_id
          }));
          
          setSuggestions(formattedSuggestions);
          setShowSuggestions(formattedSuggestions.length > 0);
        } else {
          // Fallback to basic suggestions if API doesn't return results
          let fallbackSuggestions = [];
          
          if (field === 'city') {
            fallbackSuggestions = [
              { value: 'San Francisco', label: 'San Francisco, CA' },
              { value: 'San Jose', label: 'San Jose, CA' },
              { value: 'San Diego', label: 'San Diego, CA' }
            ].filter(item => item.value.toLowerCase().includes(query.toLowerCase()));
          } else if (field === 'state') {
            fallbackSuggestions = [
              { value: 'CA', label: 'California' },
              { value: 'NY', label: 'New York' },
              { value: 'TX', label: 'Texas' }
            ].filter(item =>
              item.value.toLowerCase().includes(query.toLowerCase()) ||
              item.label.toLowerCase().includes(query.toLowerCase())
            );
          } else if (field === 'country') {
            fallbackSuggestions = [
              { value: 'USA', label: 'United States' },
              { value: 'CAN', label: 'Canada' },
              { value: 'UK', label: 'United Kingdom' }
            ].filter(item =>
              item.value.toLowerCase().includes(query.toLowerCase()) ||
              item.label.toLowerCase().includes(query.toLowerCase())
            );
          }
          
          setSuggestions(fallbackSuggestions);
          setShowSuggestions(fallbackSuggestions.length > 0);
        }
      })
      .catch(error => {
        console.error('Error fetching location suggestions:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      });
  };
  
  // Debounced search function with a shorter delay for better responsiveness
  const debouncedSearch = debounce(searchLocationSuggestions, 300);
  
  // Handle input focus
  const handleInputFocus = (field) => {
    setIsFocused(true);
    setActiveField(field);
    setActiveSuggestionIndex(-1);
  };
  
  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    // Arrow down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    }
    // Arrow up
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex(prev =>
        prev > 0 ? prev - 1 : 0
      );
    }
    // Enter
    else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionSelect(suggestions[activeSuggestionIndex]);
    }
    // Escape
    else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };
  
  // Handle selecting a suggestion
  const handleSuggestionSelect = (suggestion) => {
    // First, update the current field with the selected value
    // This ensures the field gets updated immediately
    const currentField = activeField;
    handleFieldChange(currentField, suggestion.value);
    
    // Close the suggestions dropdown
    setShowSuggestions(false);
    
    // If we have a placeId, use it to get detailed location information
    // But do it in a non-blocking way
    if (suggestion.placeId) {
      // Call getLocation with the placeId without blocking the UI
      getLocation({ place: suggestion.placeId })
        .then(locationResponse => {
          console.log('Location details response:', locationResponse); // Debug log
          
          // Handle different response formats
          const locationDetails = locationResponse?.result || locationResponse;
          
          if (locationDetails) {
            // Create a new location object with the detailed information
            // Important: Get the CURRENT state of locationData, not the closure value
            const updatedLocation = { ...locationData };
            
            // Extract address components from the response
            if (locationDetails.address_components) {
              locationDetails.address_components.forEach(component => {
                const type = component.types[0];
                
                switch (type) {
                  case 'street_number':
                    updatedLocation.street = component.long_name + (updatedLocation.street ? ' ' + updatedLocation.street : '');
                    break;
                  case 'route':
                    updatedLocation.street = (updatedLocation.street ? updatedLocation.street + ' ' : '') + component.long_name;
                    break;
                  case 'locality':
                    updatedLocation.city = component.long_name;
                    break;
                  case 'administrative_area_level_1':
                    updatedLocation.state = component.short_name;
                    break;
                  case 'postal_code':
                    updatedLocation.zip = component.long_name;
                    break;
                  case 'country':
                    updatedLocation.country = component.long_name;
                    break;
                  case 'administrative_area_level_2':
                    updatedLocation.county = component.long_name;
                    break;
                }
              });
            }
            
            // Set coordinates if available
            if (locationDetails.geometry && locationDetails.geometry.location) {
              updatedLocation.lat = locationDetails.geometry.location.lat;
              updatedLocation.lng = locationDetails.geometry.location.lng;
            }
            
            // Set formatted address if available and we're in the address field
            if (locationDetails.formatted_address && currentField === 'address') {
              updatedLocation.address = locationDetails.formatted_address;
            }
            
            // Handle alternative response format
            if (locationResponse?.Location) {
              if (!updatedLocation.lat && locationResponse.Location.Latitude) {
                updatedLocation.lat = locationResponse.Location.Latitude;
              }
              if (!updatedLocation.lng && locationResponse.Location.Longitude) {
                updatedLocation.lng = locationResponse.Location.Longitude;
              }
            }
            
            // Update the location data without blocking the UI
            setLocationData(updatedLocation);
            onChange(JSON.stringify(updatedLocation));
          }
        })
        .catch(error => {
          console.error('Error fetching location details:', error);
          
          // Fallback to basic parsing if API call fails
          if (currentField === 'address' && suggestion.value) {
            const parts = suggestion.value.split(',');
            if (parts.length >= 3) {
              // Assuming format: "Street, City, State ZIP"
              const city = parts[1]?.trim();
              const stateZip = parts[2]?.trim().split(' ');
              const state = stateZip[0]?.trim();
              const country = parts[parts.length - 1]?.trim();
              
              // Only update these fields if they're empty
              if (city && !locationData.city) handleFieldChange('city', city);
              if (state && !locationData.state) handleFieldChange('state', state);
              if (country && !locationData.country && parts.length > 3) handleFieldChange('country', country);
            }
          }
        });
    } else {
      // Fallback to basic parsing if no placeId is available
      if (currentField === 'address' && suggestion.value) {
        const parts = suggestion.value.split(',');
        if (parts.length >= 3) {
          // Assuming format: "Street, City, State ZIP"
          const city = parts[1]?.trim();
          const stateZip = parts[2]?.trim().split(' ');
          const state = stateZip[0]?.trim();
          const country = parts[parts.length - 1]?.trim();
          
          // Only update these fields if they're empty
          if (city && !locationData.city) handleFieldChange('city', city);
          if (state && !locationData.state) handleFieldChange('state', state);
          if (country && !locationData.country && parts.length > 3) handleFieldChange('country', country);
        }
      }
    }
  };
  
  // Convert hex color to RGB for CSS variables
  const hexToRgb = (hex) => {
    // Remove the # if present
    hex = hex.replace('#', '');
    
    // Parse the hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `${r}, ${g}, ${b}`;
  };
  
  // Style for accent color
  const accentStyle = menuColor ? {
    borderLeft: `3px solid ${menuColor}`,
    paddingLeft: 5
  } : {};
  
  return (
    <div className="location-fields" style={{
      '--accent-color': menuColor,
      '--accent-color-rgb': menuColor ? hexToRgb(menuColor) : '24, 144, 255' // Default to Ant Design's primary blue
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', backgroundColor: menuColor }} />
      <Form layout="vertical" className="location-form" size="small">
        <Row gutter={[12, 4]}>
          <Col span={24}>
            <Space align="center" style={{ marginBottom: 8 }}>
              <Switch
                size="small"
                checked={locationData.isRemote}
                onChange={(checked) => handleFieldChange('isRemote', checked)}
                disabled={loading}
              />
              <span>Remote Employee</span>
              {locationData.isRemote && (
                <Tag color="blue" icon={<EnvironmentOutlined />}>Working Remotely</Tag>
              )}
              {(locationData.lat || locationData.lon || locationData.lng) && (
                <CoordinatesPopover coordinates={locationData} menuColor={menuColor} />
              )}
            </Space>
          </Col>
          
          <Col span={12}>
            <Form.Item label="Address" style={{ marginBottom: 8, ...accentStyle }}>
              <div className="autocomplete-wrapper" style={{ position: 'relative' }}>
                <Input
                  size="small"
                  value={locationData.address}
                  onChange={(e) => {
                    handleFieldChange('address', e.target.value);
                    debouncedSearch(e.target.value, 'address');
                  }}
                  placeholder="Street address"
                  disabled={loading} // Don't disable during autocomplete
                  onFocus={() => handleInputFocus('address')}
                  onKeyDown={handleKeyDown}
                  suffix={null} // Remove the loading spinner to prevent UI jitter
                />
                {showSuggestions && activeField === 'address' && (
                  <div className="suggestions-dropdown">
                    {suggestions.length > 0 ? (
                      suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className={`suggestion-item ${index === activeSuggestionIndex ? 'active' : ''} ${menuColor ? 'theme-highlight' : ''}`}
                          onClick={() => handleSuggestionSelect(suggestion)}
                          onMouseEnter={() => setActiveSuggestionIndex(index)}
                        >
                          {suggestion.label}
                        </div>
                      ))
                    ) : (
                      <div className="suggestion-item no-results">
                        No suggestions found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item label="Street" style={{ marginBottom: 8 }}>
              <Input
                size="small"
                value={locationData.street}
                onChange={(e) => handleFieldChange('street', e.target.value)}
                placeholder="Street name"
                disabled={loading} // Only disable during form loading, not during autocomplete
                onFocus={() => handleInputFocus('street')}
                suffix={null} // Remove the loading spinner to prevent UI jitter
              />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={[12, 4]}>
          <Col span={8}>
            <Form.Item label="City" style={{ marginBottom: 8, ...accentStyle }}>
              <div className="autocomplete-wrapper" style={{ position: 'relative' }}>
                <Input
                  size="small"
                  value={locationData.city}
                  onChange={(e) => {
                    handleFieldChange('city', e.target.value);
                    debouncedSearch(e.target.value, 'city');
                  }}
                  placeholder="City"
                  disabled={loading} // Don't disable during autocomplete
                  onFocus={() => handleInputFocus('city')}
                  onKeyDown={handleKeyDown}
                  suffix={null} // Remove the loading spinner to prevent UI jitter
                />
                {showSuggestions && activeField === 'city' && (
                  <div className="suggestions-dropdown">
                    {suggestions.length > 0 ? (
                      suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className={`suggestion-item ${index === activeSuggestionIndex ? 'active' : ''} ${menuColor ? 'theme-highlight' : ''}`}
                          onClick={() => handleSuggestionSelect(suggestion)}
                          onMouseEnter={() => setActiveSuggestionIndex(index)}
                        >
                          {suggestion.label}
                        </div>
                      ))
                    ) : (
                      <div className="suggestion-item no-results">
                        No suggestions found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item label="State" style={{ marginBottom: 8 }}>
              <div className="autocomplete-wrapper" style={{ position: 'relative' }}>
                <Input
                  size="small"
                  value={locationData.state}
                  onChange={(e) => {
                    handleFieldChange('state', e.target.value);
                    debouncedSearch(e.target.value, 'state');
                  }}
                  placeholder="State"
                  disabled={loading} // Don't disable during autocomplete
                  onFocus={() => handleInputFocus('state')}
                  onKeyDown={handleKeyDown}
                  suffix={null} // Remove the loading spinner to prevent UI jitter
                />
                {showSuggestions && activeField === 'state' && (
                  <div className="suggestions-dropdown">
                    {suggestions.length > 0 ? (
                      suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className={`suggestion-item ${index === activeSuggestionIndex ? 'active' : ''} ${menuColor ? 'theme-highlight' : ''}`}
                          onClick={() => handleSuggestionSelect(suggestion)}
                          onMouseEnter={() => setActiveSuggestionIndex(index)}
                        >
                          {suggestion.label}
                        </div>
                      ))
                    ) : (
                      <div className="suggestion-item no-results">
                        No suggestions found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item label="ZIP" style={{ marginBottom: 8 }}>
              <Input
                size="small"
                value={locationData.zip}
                onChange={(e) => handleFieldChange('zip', e.target.value)}
                placeholder="ZIP code"
                disabled={loading} // Don't disable during autocomplete
                onFocus={() => handleInputFocus('zip')}
                suffix={null} // No loading indicator needed for ZIP
              />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={[12, 4]}>
          <Col span={12}>
            <Form.Item label="County" style={{ marginBottom: 8 }}>
              <Input
                size="small"
                value={locationData.county}
                onChange={(e) => handleFieldChange('county', e.target.value)}
                placeholder="County"
                disabled={loading} // Don't disable during autocomplete
                onFocus={() => handleInputFocus('county')}
                suffix={null} // No loading indicator needed for county
              />
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item label="Country" className="accent-form-item" style={{ marginBottom: 8, ...accentStyle }}>
              <div className="autocomplete-wrapper" style={{ position: 'relative' }}>
                <Input
                  size="small"
                  value={locationData.country}
                  onChange={(e) => {
                    handleFieldChange('country', e.target.value);
                    debouncedSearch(e.target.value, 'country');
                  }}
                  placeholder="Country"
                  disabled={loading} // Don't disable during autocomplete
                  onFocus={() => handleInputFocus('country')}
                  onKeyDown={handleKeyDown}
                  suffix={null} // Remove the loading spinner to prevent UI jitter
                />
                {showSuggestions && activeField === 'country' && (
                  <div className="suggestions-dropdown">
                    {suggestions.length > 0 ? (
                      suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className={`suggestion-item ${index === activeSuggestionIndex ? 'active' : ''} ${menuColor ? 'theme-highlight' : ''}`}
                          onClick={() => handleSuggestionSelect(suggestion)}
                          onMouseEnter={() => setActiveSuggestionIndex(index)}
                        >
                          {suggestion.label}
                        </div>
                      ))
                    ) : (
                      <div className="suggestion-item no-results">
                        No suggestions found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

// Budget fields component
const BudgetFields = ({ user, currentUser, onAddBudget, loading }) => {
  const [addingBudget, setAddingBudget] = useState(false);
  const [additionalAmount, setAdditionalAmount] = useState(0);
  const [form] = Form.useForm();
  
  // Calculate budget values
  const recognitionEvents = user?.recognitionEvents || {};
  const currentBudget = recognitionEvents?.currentBudget ?? currentUser?.company?.recognitionSettings?.baseSnappyBudget ?? 0;
  const amountPending = recognitionEvents?.amountPending || 0;
  const additionalBudgetTotal = recognitionEvents?.additionalBudgetTotal || 0;
  const availableBudget = currentBudget - amountPending;
  
  // Get recent events - handle both array and single object cases
  let recentEvents = [];
  if (recognitionEvents?.eventList) {
    if (Array.isArray(recognitionEvents.eventList)) {
      recentEvents = recognitionEvents.eventList;
    } else {
      // If eventList is a single object, wrap it in an array
      recentEvents = [recognitionEvents.eventList];
    }
  }
  
  // Handle adding budget
  const handleAddBudget = () => {
    if (additionalAmount <= 0) return;
    
    // Create transaction object with all required fields
    const newTransaction = {
      adminOverride: true,
      budgetAfterTotal: currentBudget + additionalAmount,
      budgetBeforeTotal: currentBudget,
      completedById: currentUser?.id,
      createdDate: new Date().toISOString(),
      recognitionAmount: additionalAmount,
      recognitionId: `admin-override-${Date.now()}`,
      recognitionName: "Budget Adjustment",
    };
    
    onAddBudget(newTransaction);
    setAddingBudget(false);
    setAdditionalAmount(0);
    form.resetFields();
  };
  
  // Table columns for recent events
  const columns = [
    {
      title: 'Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (date) => dayjs(date).format('MM/DD/YY'),
      width: '20%',
    },
    {
      title: 'Type',
      dataIndex: 'adminOverride',
      key: 'type',
      render: (isAdmin) => {
        const tag = isAdmin
          ? (<Tag color="blue" size="small">Override</Tag>)
          : (<Tag color="green" size="small">Reward</Tag>);
          return tag;
      },
      width: '20%',
    },
    {
      title: 'Amount',
      dataIndex: 'recognitionAmount',
      key: 'amount',
      render: (amount) => `$${amount}`,
      width: '20%',
    },
    {
      title: 'Name',
      dataIndex: 'recognitionName',
      key: 'name',
      ellipsis: true,
      width: '40%',
    }
  ];
  
  return (
    <div className="budget-fields">
      <Row gutter={[12, 12]}>
        <Col span={8}>
          <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
            <Statistic
              title="Current Budget"
              value={currentBudget}
              precision={0}
              prefix="$"
              valueStyle={{ color: '#3f8600', fontSize: '18px' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
            <Statistic
              title="Amount Pending"
              value={amountPending}
              precision={0}
              prefix="$"
              valueStyle={{ color: '#cf1322', fontSize: '18px' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
            <Statistic
              title="Available Budget"
              value={availableBudget}
              precision={0}
              prefix="$"
              valueStyle={{
                color: availableBudget > 0 ? '#3f8600' : '#cf1322',
                fontSize: '18px'
              }}
            />
          </Card>
        </Col>
      </Row>
      
      {additionalBudgetTotal > 0 && (
        <div className="additional-budget-info">
          <Text type="secondary" style={{ fontSize: '12px' }}>Additional budget total: ${additionalBudgetTotal}</Text>
        </div>
      )}
      
      <Row style={{ marginTop: 8, marginBottom: 8 }}>
        <Col span={24}>
          {!addingBudget ? (
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setAddingBudget(true)}
              disabled={loading}
            >
              Add Budget
            </Button>
          ) : (
            <Form form={form} layout="inline" size="small">
              <Form.Item label="Amount" name="amount" rules={[{ required: true, message: 'Enter amount' }]}>
                <InputNumber
                  min={1}
                  prefix="$"
                  value={additionalAmount}
                  onChange={setAdditionalAmount}
                  style={{ width: 100 }}
                  size="small"
                />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" size="small" onClick={handleAddBudget} disabled={additionalAmount <= 0 || loading}>
                    Add
                  </Button>
                  <Button size="small" onClick={() => setAddingBudget(false)}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </Col>
      </Row>
      
      <div className="recent-events">
        <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>Recent Events</Text>
        <Table
          dataSource={recentEvents.map((event, index) => ({
            ...event,
            uniqueKey: `${event.recognitionId || ''}-${index}`
          }))}
          columns={columns}
          rowKey="uniqueKey"
          size="small"
          pagination={{ pageSize: 3, size: 'small' }}
          loading={loading}
          locale={{ emptyText: 'No transaction history' }}
          style={{ fontSize: '12px' }}
        />
      </div>
    </div>
  );
};

const EmployeeDetailsModal = ({
  visible,
  onCancel,
  employeeId,
  user,
  onUpdate,
  currentUser,
  departments,
  userGroups
}) => {
  const [loading, setLoading] = useState(false);
  const [saveFields, setSaveFields] = useState({});
  const [avatarUrl, setAvatarUrl] = useState(null);
  
  // Get the query client for debugging
 // const queryClient = useQueryClient();
  
  // // Use the useEmployeeData hook to fetch employee data
  // const {
  //   employeeData: tanstackEmployeeData,
  //   isLoading: isLoadingEmployee,
  //   isError: isEmployeeError,
  //   error: employeeError,
  //   refetch: refetchEmployee,
  //   invalidateEmployeeData
  // } = useEmployeeData(employeeId, {
  //   // Don't automatically fetch data, we'll use the existing data flow
  //   enabled: false,
  // });
  
  // Extract company theme color for accent styling
  const menuColor = currentUser?.company?.theme?.menuColor || '#1890ff';

  // Reset save fields when modal opens with new employee
  useEffect(() => {
    setSaveFields({});
  }, [employeeId]);

  useEffect(() => {
    if (!user) return;
    
    const asyncAvatar = async () => {
      let newAvatarUrl;
      if (user?.avatar?.key) {
        newAvatarUrl = await downloadFromS3Signed(user?.avatar?.key );
      }
      if (newAvatarUrl) {
        setAvatarUrl(newAvatarUrl);
      }
    };
    asyncAvatar();
  },[user?.avatar])

  const handleSave = async () => {
    if (Object.keys(saveFields).length === 0) {
      onCancel();
      return;
    }

    setLoading(true);
    try {
      await onUpdate({
          id: user?.id,
          ...saveFields
        });
      message.success('Employee details updated successfully');
      setSaveFields({});
      onCancel();
    } catch (error) {
      console.error('Error updating employee:', error);
      message.error('Failed to update employee details');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setSaveFields(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!user) {
    return (
      <Modal
        title="Employee Details"
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={700}
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading employee details...</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Title level={4} style={{ margin: 0 }}>Employee Details</Title>
        </motion.div>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      className="employee-details-modal"
      footer={[
        <Button
          key="refresh"
          onClick={() => {
            // Show a loading message
            message.loading('Refreshing employee data...', 0.5);
            
            // Invalidate the employee data cache to force a refetch
            invalidateEmployeeData();
            
            // Refetch the employee data
            refetchEmployee().then(result => {
              if (result.isSuccess) {
                message.success('Employee data refreshed successfully');
                
                // Log the data to the console for debugging
                console.log('Refreshed employee data:', result.data);
                
                // You could update the UI here if needed
                // For now, we'll just show a success message
              } else {
                message.error('Failed to refresh employee data');
              }
            });
          }}
          icon={<SyncOutlined />}
          style={{ marginRight: 'auto' }}
        >
          Refresh Data
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={loading}
          disabled={Object.keys(saveFields).length === 0}
        >
          Save Changes
        </Button>
      ]}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="employee-details-container"
      >
        {/* Header with Avatar */}
        <Row gutter={24} className="employee-header">
          <Col span={4}>
            <div className="employee-avatar">
              <Avatar
                size={80}
                src={avatarUrl}
                icon={!avatarUrl && <UserOutlined />}
                style={{
                  backgroundColor: !avatarUrl ? '#1890ff' : 'transparent'
                }}
              />
            </div>
          </Col>
          <Col span={20}>
            <div className="employee-header-info">
              <Text strong style={{ fontSize: 18 }}>
                {user?.firstName} {user?.lastName}
              </Text>
              <Text type="secondary" style={{ display: 'block' }}>
                {user?.title || user?.role}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ID: {user?.employeeId} • {user?.active ? 'Active' : 'Inactive'} • Created: {user?.dateCreated ? new Date(user?.dateCreated).toLocaleDateString() : 'N/A'}
              </Text>
            </div>
          </Col>
        </Row>

        <Divider style={{ margin: '12px 0' }} />

        {/* Main Content - Two Column Layout */}
        <Row gutter={24} className="employee-content">
          {/* Left Column - Personal Details */}
          <Col span={12} className="left-column">
            <div className="section-title section-title-accent" style={{ borderLeftColor: menuColor }}>
              <Text strong>Personal Information</Text>
            </div>
            
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <EditableField
                  label="First Name"
                  value={user?.firstName}
                  onSave={(value) => handleFieldChange('firstName', value)}
                  loading={loading}
                />
              </Col>
              <Col span={12}>
                <EditableField
                  label="Last Name"
                  value={user?.lastName}
                  onSave={(value) => handleFieldChange('lastName', value)}
                  loading={loading}
                />
              </Col>
            </Row>
            
            <Row gutter={[16, 8]}>
              <Col span={24}>
                <EditableField
                  label="Email"
                  value={user?.emailAddress}
                  onSave={(value) => handleFieldChange('emailAddress', value)}
                  loading={loading}
                />
              </Col>
            </Row>
            
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <EditableField
                  label="Job Title"
                  value={user?.title}
                  onSave={(value) => handleFieldChange('title', value)}
                  loading={loading}
                />
              </Col>
              <Col span={12}>
                <EditableField
                  label="Employee ID"
                  value={user?.employeeId}
                  onSave={(value) => handleFieldChange('employeeId', value)}
                  loading={loading}
                  menuColor={menuColor}
                />
              </Col>
            </Row>
            
            <div className="section-title section-title-accent" style={{ marginTop: 16, borderLeftColor: menuColor }}>
              <Text strong>Location</Text>
            </div>
            
            <LocationFields
              location={user?.location}
              onChange={(value) => handleFieldChange('location', value)}
              loading={loading}
              menuColor={menuColor}
            />
          </Col>
          
          {/* Right Column - Department & Budget */}
          <Col span={12} className="right-column">
            <div className="section-title section-title-accent" style={{ borderLeftColor: menuColor }}>
              <Text strong>Department Information</Text>
            </div>
            
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <SelectField
                  label="Department"
                  value={user.departmentId}
                  options={(departments || []).map(dept => ({
                    label: dept.name,
                    value: dept.id
                  }))}
                  onSave={(value) => handleFieldChange('departmentId', value)}
                  loading={loading}
                />
              </Col>
              <Col span={12}>
                <SelectField
                  label="User Group"
                  value={user.userGroupId}
                  options={(userGroups || []).map(group => ({
                    label: group.name,
                    value: group.id
                  }))}
                  onSave={(value) => handleFieldChange('userGroupId', value)}
                  loading={loading}
                />
              </Col>
            </Row>
            
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <EditableField
                  label="Points"
                  value={user?.points}
                  editable={false}
                  loading={loading}
                />
              </Col>
            </Row>
            
            <div className="section-title section-title-accent" style={{ marginTop: 16, borderLeftColor: menuColor }}>
              <Text strong>Budget Information</Text>
            </div>
            
            <BudgetFields
              user={user}
              currentUser={currentUser}
              onAddBudget={(transaction) => {
                // Create a new recognitionEvents object with a single eventList object
                const currentEvents = user?.recognitionEvents || {};
                const currentBudget = currentEvents?.currentBudget || 0;
                const additionalBudgetTotal = currentEvents?.additionalBudgetTotal || 0;
                const amountPending = currentEvents?.amountPending || 0;
                
                // Calculate new budget values
                const newCurrentBudget = transaction.budgetAfterTotal;
                const newAdditionalBudget = additionalBudgetTotal + transaction.recognitionAmount;
                
                const newRecognitionEvents = {
                  hasSeenPopup: currentEvents?.hasSeenPopup || false,
                  currentBudget: newCurrentBudget,
                  additionalBudgetTotal: newAdditionalBudget,
                  amountPending: amountPending,
                  lastUpdated: new Date().toISOString(),
                  eventList: transaction
                };
                
                // Update the user object
                handleFieldChange('recognitionEvents', newRecognitionEvents);
              }}
              loading={loading}
            />
            
            {/* TanStack Query Debug Section */}
            {/* {process.env.NODE_ENV === 'development' && (
              <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
                <div className="section-title" style={{ marginBottom: 8 }}>
                  <Text strong>TanStack Query Debug</Text>
                </div>
                
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary">Query Status:</Text>{' '}
                    {isLoadingEmployee ? (
                      <Tag color="processing">Loading</Tag>
                    ) : isEmployeeError ? (
                      <Tag color="error">Error</Tag>
                    ) : tanstackEmployeeData ? (
                      <Tag color="success">Success</Tag>
                    ) : (
                      <Tag color="default">Idle</Tag>
                    )}
                  </div>
                  
                  {isEmployeeError && (
                    <div>
                      <Text type="danger">Error: {employeeError?.message}</Text>
                    </div>
                  )}
                  
                  <Space>
                    <Button
                      size="small"
                      onClick={() => refetchEmployee()}
                      icon={<SyncOutlined />}
                    >
                      Refetch
                    </Button>
                    
                    <Button
                      size="small"
                      onClick={() => {
                        // Log the current query cache to the console
                        console.log('Query Cache:', queryClient.getQueryCache().getAll());
                        message.info('Query cache logged to console');
                      }}
                    >
                      Log Cache
                    </Button>
                  </Space>
                </Space>
              </div>
            )} */}
          </Col>
        </Row>
      </motion.div>
    </Modal>
  );
};

export default withApollo(EmployeeDetailsModal);